import { useState, useMemo, useEffect } from 'react'
import { useBudgets, useAccounts, useIncome, useQuickSpends, useQuickIncome, useMonthlyReports, useBills } from '../hooks/useData'
import { useStore } from '../store'
import { useAuth } from '../hooks/useAuth'
import { currentPayCycleStart, getPayCycle, cyclesForPicker, cycleRangeLabel, billDueDateInMonth } from '../lib/payday'
import { GoldButton, GhostButton, Modal, Input, Select, Loading, fmt, fmt2, fmtPct, CyclePicker, colors, fonts } from '../components/UI'
import QuickSpendModal from '../components/QuickSpendModal'
import Hint from '../components/Hint'
import BillLogo from '../components/BillLogo'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'

const DEFAULT_CATEGORIES = [
  'Housing','Groceries','Eating Out','Transport','Utilities','Entertainment',
  'Subscriptions','Clothing','Health','Personal Care','Baby','Pet',
  'Business','Loan','Bills','Savings','Investments','Insurance','Other'
]
const NEEDS_CATS = ['Housing','Utilities','Transport','Health','Groceries','Insurance','Loan','Bills']
const SAVINGS_CATS = ['Savings','Investments']

function catBucket(cat, savedBucket) {
  if (savedBucket) return savedBucket
  if (NEEDS_CATS.includes(cat)) return 'Essential'
  if (SAVINGS_CATS.includes(cat)) return 'Savings'
  return 'Want'
}

const CAT_STYLE = {
  'Housing':       { color: '#4a8fe8', emoji: '🏠' },
  'Groceries':     { color: '#3db87a', emoji: '🛒' },
  'Eating Out':    { color: '#f97316', emoji: '🍽️' },
  'Transport':     { color: '#6b8cba', emoji: '🚗' },
  'Utilities':     { color: '#e05252', emoji: '⚡' },
  'Subscriptions': { color: '#8b5cf6', emoji: '📱' },
  'Insurance':     { color: '#6366f1', emoji: '🛡️' },
  'Health':        { color: '#ec4899', emoji: '💊' },
  'Entertainment': { color: '#f97316', emoji: '🎮' },
  'Savings':       { color: '#c9972b', emoji: '🏦' },
  'Investments':   { color: '#14b8a6', emoji: '📈' },
  'Clothing':      { color: '#e879f9', emoji: '👗' },
  'Personal Care': { color: '#fb7185', emoji: '🪥' },
  'Baby':          { color: '#60a5fa', emoji: '🍼' },
  'Pet':           { color: '#a78bfa', emoji: '🐾' },
  'Business':      { color: '#34d399', emoji: '💼' },
  'Loan':          { color: '#f59e0b', emoji: '🏦' },
  'Bills':         { color: '#64748b', emoji: '🧾' },
  'Other':         { color: '#64748b', emoji: '🔵' },
}
function catColor(cat) { return CAT_STYLE[cat]?.color || '#64748b' }
function catEmoji(cat) { return CAT_STYLE[cat]?.emoji || '🔵' }

// Left-accent tile (matches Budget mockup style)
function BudgetTile({ label, value, sub, accent, valueColor }) {
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${accent}`, borderRadius: '12px', padding: '14px 14px 14px 12px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>
      <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '6px' }}>{label}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: valueColor || colors.text, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '4px' }}>{sub}</div>
    </div>
  )
}

export default function BudgetPage({ onNavigate }) {
  const { profile } = useAuth()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const isDual = profile?.income_type === 'dual'
  const person1 = profile?.income_1_label || 'Me'
  const person2 = profile?.income_2_label || 'Partner'

  const cycles = useMemo(() => cyclesForPicker(payDay, shiftWeekends), [payDay, shiftWeekends])
  const currentCycle = useMemo(() => getPayCycle(payDay, new Date(), shiftWeekends), [payDay, shiftWeekends])
  const [selectedCycleStart, setSelectedCycleStart] = useState(() => currentCycle?.startIso || '')
  const selectedCycle = useMemo(() => cycles.find(c => c.startIso === selectedCycleStart) || currentCycle, [cycles, selectedCycleStart, currentCycle])
  const cycleStartKey = currentPayCycleStart(payDay, new Date(), shiftWeekends)

  // selectedCycleStart is seeded once from currentCycle, which may not exist on the
  // first render (pay day still loading). Sync it once the cycle is available so
  // budgets load for the right cycle instead of staying empty or stuck loading.
  useEffect(() => {
    if (!selectedCycleStart && currentCycle?.startIso) setSelectedCycleStart(currentCycle.startIso)
  }, [currentCycle, selectedCycleStart])

  const { budgets, loading, saveBudget, deleteBudget, totalBudgeted } = useBudgets(selectedCycleStart)
  const { incomeEntries, incomeForCycle } = useIncome()
  const { accounts, upsertAccount, deleteAccount, refresh: refreshAccounts } = useAccounts()
  const { cycleSpends, cycleTotal, categorySpends, clearQuickSpendsForCycle } = useQuickSpends(cycleStartKey)
  const { cycleTotal: quickIncomeTotal } = useQuickIncome(cycleStartKey)
  const displayIncome = incomeEntries.length > 0 ? incomeForCycle(selectedCycle) + quickIncomeTotal : quickIncomeTotal

  // Group ALL quick income by year-month for the Over time tab
  const allQuickIncome = useStore(s => s.quickIncome)
  const quickIncomeByMonth = useMemo(() => {
    return allQuickIncome.reduce((acc, e) => {
      const month = e.pay_cycle_start?.slice(0, 7)
      if (month) acc[month] = (acc[month] || 0) + (e.amount || 0)
      return acc
    }, {})
  }, [allQuickIncome])
  const { bills } = useBills()

  // Bills that fall within the selected cycle, grouped by category
  // These automatically count toward SPENT so users don't need to manually log recurring bills
  const billSpentByCategory = useMemo(() => {
    if (!selectedCycle) return {}
    const start = new Date(selectedCycle.startIso)
    const end = new Date(selectedCycle.endIso)
    const result = {}
    for (const bill of bills) {
      if (!bill.category || !bill.amount) continue
      for (let m = 0; m < 3; m++) {
        const probe = new Date(start.getFullYear(), start.getMonth() + m, 1)
        const dueDate = billDueDateInMonth(bill, probe.getFullYear(), probe.getMonth())
        if (dueDate && dueDate >= start && dueDate <= end) {
          result[bill.category] = (result[bill.category] || 0) + bill.amount
          break
        }
      }
    }
    return result
  }, [bills, selectedCycle])
  const { reports } = useMonthlyReports()

  const [showBudgetModal, setShowBudgetModal] = useState(false)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [showQuickSpend, setShowQuickSpend] = useState(false)
  const [showAccounts, setShowAccounts] = useState(true)
  const [showCyclePicker, setShowCyclePicker] = useState(false)
  const [budgetView, setBudgetView] = useState('cycle')
  const [editingAccount, setEditingAccount] = useState(null)
  const [quickBalanceAccount, setQuickBalanceAccount] = useState(null)
  const [quickBalanceValue, setQuickBalanceValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [pendingClear, setPendingClear] = useState(null)
  const [budgetForm, setBudgetForm] = useState({ category: DEFAULT_CATEGORIES[0], budgeted: '', bucket: '' })
  const [editingBudget, setEditingBudget] = useState(null)
  const [accountForm, setAccountForm] = useState({ name: '', type: 'current', balance: '', credit_limit: '', is_emergency_fund: false, owner_label: 'Joint' })

  async function handleSaveBudget() {
    setSaving(true)
    await saveBudget(budgetForm.category, parseFloat(budgetForm.budgeted) || 0, 0, budgetForm.bucket)
    setSaving(false); setShowBudgetModal(false); setShowCustomInput(false)
    setBudgetForm({ category: DEFAULT_CATEGORIES[0], budgeted: '', bucket: '' })
  }
  function editBudget(b) { setEditingBudget(b); setBudgetForm({ category: b.category, budgeted: String(b.budgeted), bucket: b.bucket || '' }); setShowCustomInput(false); setShowBudgetModal(true) }

  async function handleSaveAccount() {
    if (containsSensitiveData(accountForm.name)) { setSaveError(SENSITIVE_DATA_ERROR); return }
    setSaveError(''); setSaving(true)
    const isCurrentNonEmergency = accountForm.type === 'current' && !accountForm.is_emergency_fund
    const wasEdit = !!editingAccount
    const { error } = await upsertAccount({ ...accountForm, balance: parseFloat(accountForm.balance) || 0, credit_limit: accountForm.credit_limit ? parseFloat(accountForm.credit_limit) : null, id: editingAccount?.id })
    if (!error) await refreshAccounts()
    setSaving(false); setShowAccountModal(false)
    setAccountForm({ name: '', type: 'current', balance: '', is_emergency_fund: false, owner_label: isDual ? person1 : 'Joint' })
    setEditingAccount(null)
    if (!error && wasEdit && isCurrentNonEmergency && cycleSpends.length > 0 && cycleStartKey) {
      setPendingClear({ count: cycleSpends.length, total: cycleTotal })
    }
  }
  async function confirmClearCycle() { if (cycleStartKey) await clearQuickSpendsForCycle(cycleStartKey); setPendingClear(null) }
  function openEditAccount(a) { setEditingAccount(a); setAccountForm({ name: a.name, type: a.type, balance: String(a.balance), credit_limit: a.credit_limit ? String(a.credit_limit) : '', is_emergency_fund: a.is_emergency_fund || false, owner_label: a.owner_label || 'Joint' }); setShowAccountModal(true) }

  const unallocated = displayIncome > 0 ? displayIncome - totalBudgeted : null
  const daysLeft = selectedCycle ? Math.max(0, Math.ceil((new Date(selectedCycle.endIso) - new Date()) / 86400000)) : null
  const totalBillSpent = Object.values(billSpentByCategory).reduce((s, v) => s + v, 0)
  const totalSpent = cycleTotal + totalBillSpent
  const spendingPct = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0

  // No pay date set (it is optional at onboarding): the Budget runs on pay cycles,
  // so without one there is nothing to anchor budgets to. Show a clear route to fix
  // it rather than a bare screen. Only once the profile has loaded, to avoid a flash.
  if (profile && !payDay) {
    return (
      <div style={{ padding: '40px 20px', maxWidth: '520px', margin: '0 auto' }}>
        <div style={{ background: colors.card, border: '1px solid rgba(201,151,43,0.3)', borderRadius: '16px', padding: '28px 24px', textAlign: 'center', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '22px', color: colors.text, marginBottom: '10px' }}>Add your pay date</div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '20px' }}>
            The Budget runs on your pay period. Add the day you're paid to unlock pay-period budgeting and your Safe to Spend figure.
          </div>
          <GoldButton onClick={() => onNavigate && onNavigate('settings')}>Add it in Settings</GoldButton>
        </div>
      </div>
    )
  }

  if (loading) return <Loading />

  return (
    <div style={{ paddingBottom: '32px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Budget &amp; Accounts</div>
          <button onClick={() => setShowCyclePicker(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginTop: '3px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>Pay period: {selectedCycle ? cycleRangeLabel(selectedCycle) : '-'}</span>
            <span style={{ fontSize: '12px', color: colors.muted }}>▾</span>
          </button>
        </div>
        {budgetView === 'cycle' && <button onClick={() => { setEditingBudget(null); setBudgetForm({ category: DEFAULT_CATEGORIES[0], budgeted: '', bucket: '' }); setShowBudgetModal(true) }} style={{ position: 'absolute', right: '16px', width: '32px', height: '32px', borderRadius: '50%', background: 'transparent', border: `2px solid ${colors.gold}`, color: colors.gold, fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', margin: '14px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {[{ id: 'cycle', label: 'Budget' }, { id: 'overtime', label: 'Accounts' }].map(t => (
          <button key={t.id} onClick={() => setBudgetView(t.id)} style={{ flex: 1, padding: '10px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, color: t.id === budgetView ? colors.gold : colors.muted, borderBottom: t.id === budgetView ? `2px solid ${colors.gold}` : '2px solid transparent', marginBottom: '-1px' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '0 16px' }}>

        {budgetView === 'overtime' ? (
          <>
            {/* Accounts grouped by type */}
            {(() => {
              const groups = [
                { key: 'current', label: 'Current' },
                { key: 'savings', label: 'Savings' },
                { key: 'isa', label: 'ISA' },
                { key: 'investments', label: 'Investments' },
                { key: 'pension', label: 'Pension' },
                { key: 'credit_card', label: 'Credit' },
                { key: 'other', label: 'Other' },
              ].filter(g => accounts.some(a => a.type === g.key))

              return (
                <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>MY ACCOUNTS</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: colors.muted, letterSpacing: '0.02em' }}>tap balance to edit</span>
                      <button onClick={e => { e.stopPropagation(); setEditingAccount(null); setAccountForm({ name: '', type: 'current', balance: '', credit_limit: '', is_emergency_fund: false, owner_label: isDual ? person1 : 'Joint' }); setShowAccountModal(true) }} style={{ fontSize: '13px', fontWeight: 600, color: colors.gold, background: 'none', border: 'none', cursor: 'pointer' }}>+ Add</button>
                    </div>
                  </div>
                  {groups.map(g => (
                    <div key={g.key}>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: colors.muted, padding: '6px 16px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>{g.label.toUpperCase()}</div>
                      {accounts.filter(a => a.type === g.key).map(a => {
                        const isCR = a.type === 'credit_card'
                        const utilPct = isCR && a.credit_limit ? Math.min(100, Math.round(Math.abs(a.balance) / a.credit_limit * 100)) : null
                        return (
                          <div key={a.id} onClick={() => openEditAccount(a)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                            <BillLogo name={a.name} category={a.type} size={38} radius={19} showCR={isCR} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{a.name}</div>
                              <div style={{ fontSize: '12px', fontWeight: 500, color: colors.muted, marginTop: '2px', textTransform: 'capitalize' }}>
                                {isCR ? 'Credit card' : a.type}{a.owner_label && a.owner_label !== 'Joint' ? ` · ${a.owner_label}` : ''}
                                {utilPct !== null ? ` · ${utilPct}% used` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }} onClick={e => { e.stopPropagation(); setQuickBalanceAccount(a); setQuickBalanceValue(String(Math.abs(a.balance))) }}>
                              <div style={{ fontFamily: fonts.heading, fontSize: '16px', fontWeight: 700, color: isCR ? colors.red : colors.text, borderBottom: '1px dashed rgba(201,151,43,0.4)', paddingBottom: '1px', cursor: 'pointer' }}>
                                {isCR ? `-${fmt2(Math.abs(a.balance))}` : fmt2(a.balance)}
                              </div>
                              {isCR && a.credit_limit ? <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>/ {fmt2(a.credit_limit)} limit</div> : null}
                            </div>
                            <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                  {accounts.length === 0 && (
                    <div style={{ padding: '20px 16px', textAlign: 'center', color: colors.muted, fontSize: '14px' }}>No accounts yet</div>
                  )}
                </div>
              )
            })()}
          </>) : (
          // ── This period ─────────────────────────────────────────────────
          <>

            {/* 4 tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <BudgetTile label="TOTAL BUDGETED" value={fmt(totalBudgeted)} sub={displayIncome > 0 ? `of ${fmt(displayIncome)} income` : 'No income set'} accent={colors.gold} valueColor={displayIncome > 0 && totalBudgeted > displayIncome ? colors.red : colors.text} />
              <BudgetTile label="BUDGET GAP" value={displayIncome > 0 ? `${displayIncome - totalBudgeted < 0 ? '-' : ''}${fmt(Math.abs(displayIncome - totalBudgeted))}` : '-'} sub={displayIncome > 0 && totalBudgeted < displayIncome ? `💡 ${fmt(displayIncome - totalBudgeted)} isn't budgeted yet` : displayIncome > 0 && totalBudgeted > displayIncome ? `⚠️ Exceeds income by ${fmt(Math.abs(displayIncome - totalBudgeted))}` : displayIncome > 0 ? 'Fully allocated' : 'Set income first'} accent={colors.purple} valueColor={displayIncome > 0 && totalBudgeted > displayIncome ? colors.red : displayIncome > 0 && totalBudgeted === displayIncome ? colors.green : colors.text} />
              <BudgetTile label="SPENDING SO FAR" value={fmt(totalSpent)} sub={totalBudgeted > 0 ? `${spendingPct}% of budgeted` : 'No budgets set'} accent={colors.blue} />
              <BudgetTile label="DAYS LEFT IN PERIOD" value={daysLeft !== null ? `${daysLeft} days` : '-'} sub={selectedCycle ? `ends ${new Date(selectedCycle.endIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} accent={colors.green} valueColor={daysLeft !== null && daysLeft <= 3 ? colors.gold : colors.text} />
            </div>

            {/* Budget categories */}
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px' }}>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>BUDGET CATEGORIES</div>
                <button onClick={() => setShowBudgetModal(true)} style={{ background: 'none', border: 'none', color: colors.gold, fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>Edit categories ✏️</button>
              </div>

              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 70px', gap: '4px', padding: '6px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.muted }}>CATEGORY</div>
                {['BUDGETED', 'SPENT', 'REMAINING'].map(h => (
                  <div key={h} style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.04em', color: colors.muted, textAlign: 'right' }}>{h}</div>
                ))}
              </div>

              {budgets.length === 0 ? (
                <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', marginBottom: '10px' }}>💼</div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted }}>No budget categories yet.</div>
                </div>
              ) : budgets.map(b => {
                const quickSpent = categorySpends[b.category] || 0
                const billSpent = billSpentByCategory[b.category] || 0
                const spent = quickSpent + billSpent
                const remaining = b.budgeted - spent
                const over = remaining < 0
                const spentPct = b.budgeted > 0 ? Math.round((spent / b.budgeted) * 100) : 0
                const remPct = b.budgeted > 0 ? Math.round((Math.abs(remaining) / b.budgeted) * 100) : 0
                const c = catColor(b.category)
                const bucket = catBucket(b.category, b.bucket)
                return (
                  <div key={b.id} onClick={() => editBudget(b)} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 70px', gap: '4px', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', alignItems: 'center', cursor: 'pointer', background: over ? 'rgba(224,82,82,0.03)' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: `${c}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                        {catEmoji(b.category)}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{b.category}</div>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: colors.muted, marginTop: '1px' }}>{bucket}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{fmt2(b.budgeted)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: spent > 0 ? (over ? colors.red : colors.text) : colors.muted }}>{spent > 0 ? fmt2(spent) : '-'}</div>
                      {spent > 0 && <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '1px' }}>{spentPct}%</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: over ? colors.red : colors.green }}>
                        {over ? `-${fmt2(Math.abs(remaining))}` : fmt2(remaining)}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: over ? colors.red : colors.green, marginTop: '1px' }}>{remPct}%</div>
                    </div>
                  </div>
                )
              })}

              <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <button onClick={() => setShowBudgetModal(true)} style={{ width: '100%', padding: '11px', background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                  + Add category
                </button>
              </div>
              {budgets.length > 0 && Object.keys(categorySpends).length === 0 && Object.keys(billSpentByCategory).length === 0 && (
                <div style={{ padding: '10px 16px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px' }}>💡</span>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, lineHeight: 1.5 }}>
                    The <strong style={{ color: colors.text, fontWeight: 600 }}>SPENT</strong> column updates when you log a quick spend (⚡) and tag it to a category.
                  </div>
                </div>
              )}
            </div>

            {/* Coaching card */}
            {budgets.length > 0 && (
              <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>🥧</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>
                    {totalSpent > 0 ? `You've spent ${spendingPct}% of your budgeted amount` : `${fmt(totalBudgeted)} committed this period`}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>
                    {totalSpent > totalBudgeted ? `${fmt(totalSpent - totalBudgeted)} over budget across categories.` : "You're on track to finish the period well."}
                  </div>
                </div>
                <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'insights' }))} style={{ background: 'none', border: 'none', color: colors.purple, fontSize: '13px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>VIEW INSIGHTS ›</button>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => setShowQuickSpend(true)} style={{ padding: '14px', background: colors.gold, border: 'none', borderRadius: '12px', fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, color: colors.onAccent, cursor: 'pointer' }}>
                + ADD SPEND
              </button>
              <button onClick={() => setShowBudgetModal(true)} style={{ padding: '14px', background: 'transparent', border: `1px solid ${colors.gold}`, borderRadius: '12px', fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, color: colors.gold, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                ✦ QUICK ADJUST
              </button>
            </div>

          </>
        )}

      </div>

      {/* Cycle picker sheet */}
      {showCyclePicker && (
        <div onClick={() => setShowCyclePicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, width: '100%', maxWidth: '480px', margin: '0 auto',  borderRadius: '24px 24px 0 0', padding: '8px 0 40px', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '8px auto 16px' }} />
            <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.text, padding: '0 20px 14px' }}>Select period</div>
            {cycles.map(c => {
              const active = c.startIso === selectedCycleStart
              return (
                <button key={c.startIso} onClick={() => { setSelectedCycleStart(c.startIso); setShowCyclePicker(false) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 20px', background: active ? 'rgba(201,151,43,0.08)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', fontFamily: fonts.body }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: active ? colors.gold : colors.text, textAlign: 'left' }}>{c.label}</div>
                    <div style={{ fontSize: '13px', color: colors.muted, marginTop: '2px', textAlign: 'left' }}>{c.rangeLabel}</div>
                  </div>
                  {active && <span style={{ color: colors.gold, fontSize: '16px' }}>✓</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick spend modal */}
      {showQuickSpend && <QuickSpendModal payCycleStart={cycleStartKey} onClose={() => setShowQuickSpend(false)} />}

      {/* Budget category modal */}
      {showBudgetModal && (
        <Modal title="Budget category" onClose={() => { setShowBudgetModal(false); setShowCustomInput(false) }}>
          {!showCustomInput ? (
            <>
              <Select label="Category" value={DEFAULT_CATEGORIES.includes(budgetForm.category) ? budgetForm.category : DEFAULT_CATEGORIES[0]} onChange={v => setBudgetForm(f => ({ ...f, category: v }))} options={DEFAULT_CATEGORIES.map(c => ({ value: c, label: c }))} />
              <button onClick={() => { setBudgetForm(f => ({ ...f, category: '' })); setShowCustomInput(true) }} style={{ background: 'none', border: 'none', color: colors.gold, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '0 0 8px', textAlign: 'left', display: 'block' }}>+ Create custom category</button>
            </>
          ) : (
            <>
              <Input label="Custom category name" value={budgetForm.category} onChange={v => setBudgetForm(f => ({ ...f, category: v }))} placeholder="e.g. Pet care" autoFocus />
              <button onClick={() => { setBudgetForm(f => ({ ...f, category: DEFAULT_CATEGORIES[0] })); setShowCustomInput(false) }} style={{ background: 'none', border: 'none', color: colors.muted, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '0 0 8px', textAlign: 'left', display: 'block' }}>← Back to categories</button>
            </>
          )}
          <Input label="Period budget" type="number" prefix="£" value={budgetForm.budgeted} onChange={v => setBudgetForm(f => ({ ...f, budgeted: v }))} placeholder="500" />
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: colors.muted, marginBottom: '8px' }}>Category type</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { id: 'Essential', label: '🔵 Essential', hint: 'Bills, rent, groceries, utilities - things you must pay. Counts toward your 50% Needs target.' },
                { id: 'Want', label: '✨ Want', hint: 'Eating out, entertainment, clothing - things you choose to spend on. Counts toward your 30% Wants target.' },
                { id: 'Savings', label: '🏦 Savings', hint: 'Savings goals, investments, emergency fund. Counts toward your 20% Savings target.' },
              ].map(b => {
                const active = (budgetForm.bucket || catBucket(budgetForm.category)) === b.id
                const col = b.id === 'Essential' ? colors.blue : b.id === 'Savings' ? colors.gold : colors.green
                return (
                  <button key={b.id} onClick={() => setBudgetForm(f => ({ ...f, bucket: b.id }))} style={{ flex: 1, padding: '8px 4px', borderRadius: '8px', border: `1px solid ${active ? col : 'rgba(255,255,255,0.1)'}`, background: active ? `${col}18` : 'transparent', color: active ? col : colors.muted, fontFamily: fonts.body, fontSize: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {b.label} <Hint title={b.id} text={b.hint} />
                  </button>
                )
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '8px' }}>
            {editingBudget && <GhostButton danger onClick={async () => { await deleteBudget(editingBudget.category); setShowBudgetModal(false); setShowCustomInput(false); setEditingBudget(null) }}>Delete</GhostButton>}
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <GhostButton onClick={() => { setShowBudgetModal(false); setShowCustomInput(false); setEditingBudget(null) }}>Cancel</GhostButton>
              <GoldButton onClick={handleSaveBudget}>{saving ? 'Saving...' : 'Save'}</GoldButton>
            </div>
          </div>
        </Modal>
      )}

      {/* Quick balance edit modal */}
      {quickBalanceAccount && (
        <Modal title={quickBalanceAccount.name} onClose={() => { setQuickBalanceAccount(null); setQuickBalanceValue('') }}>
          <div style={{ fontSize: '13px', color: colors.muted, fontFamily: fonts.body, marginBottom: '16px' }}>
            Update balance only. Tap the account row to edit other details.
          </div>
          <Input
            label={quickBalanceAccount.type === 'credit_card' ? 'Balance owed' : 'Current balance'}
            type="number"
            prefix="£"
            value={quickBalanceValue}
            onChange={v => setQuickBalanceValue(v)}
            autoFocus
            placeholder="0"
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <GhostButton onClick={() => { setQuickBalanceAccount(null); setQuickBalanceValue('') }}>Cancel</GhostButton>
            <GoldButton onClick={async () => {
              const newBalance = parseFloat(quickBalanceValue) || 0
              await upsertAccount({ ...quickBalanceAccount, balance: quickBalanceAccount.type === 'credit_card' ? -Math.abs(newBalance) : newBalance })
              await refreshAccounts()
              setQuickBalanceAccount(null)
              setQuickBalanceValue('')
            }}>{saving ? 'Saving...' : 'Save'}</GoldButton>
          </div>
        </Modal>
      )}

      {/* Account modal */}
      {showAccountModal && (
        <Modal title={editingAccount ? 'Edit account' : 'Add account'} onClose={() => { setShowAccountModal(false); setEditingAccount(null) }}>
          <Input label="Account name" value={accountForm.name} onChange={v => setAccountForm(f => ({ ...f, name: v }))} placeholder="e.g. Barclays Current" />
          <Select label="Type" value={accountForm.type} onChange={v => setAccountForm(f => ({ ...f, type: v }))} options={[{ value: 'current', label: 'Current' }, { value: 'savings', label: 'Savings' }, { value: 'isa', label: 'ISA' }, { value: 'investments', label: 'Investments' }, { value: 'pension', label: 'Pension' }, { value: 'credit_card', label: 'Credit card' }, { value: 'other', label: 'Other' }]} />
          <Input label={accountForm.type === 'credit_card' ? 'Balance owed (enter as positive)' : 'Balance'} type="number" prefix="£" value={accountForm.balance} onChange={v => setAccountForm(f => ({ ...f, balance: v }))} placeholder="0" />
          {accountForm.type === 'credit_card' && <Input label="Credit limit (optional)" type="number" prefix="£" value={accountForm.credit_limit} onChange={v => setAccountForm(f => ({ ...f, credit_limit: v }))} placeholder="e.g. 2000" />}
          {isDual && <Select label="Owner" value={accountForm.owner_label} onChange={v => setAccountForm(f => ({ ...f, owner_label: v }))} options={[{ value: person1, label: person1 }, { value: person2, label: person2 }, { value: 'Joint', label: 'Joint' }]} />}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 12px' }}>
            <div>
              <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>Emergency fund</div>
              <div style={{ fontSize: '13px', color: colors.muted, marginTop: '2px' }}>Excludes from safe-to-spend</div>
            </div>
            <button onClick={() => setAccountForm(f => ({ ...f, is_emergency_fund: !f.is_emergency_fund }))} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: accountForm.is_emergency_fund ? colors.gold : 'rgba(255,255,255,0.15)', position: 'relative', flexShrink: 0 }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: accountForm.is_emergency_fund ? '23px' : '3px', transition: 'left 0.2s' }} />
            </button>
          </div>
          {saveError && <div style={{ fontSize: '13px', color: colors.red, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '8px', marginBottom: '10px' }}>{saveError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '8px' }}>
            {editingAccount && <GhostButton danger onClick={async () => { await deleteAccount(editingAccount.id); setShowAccountModal(false); setEditingAccount(null) }}>Delete</GhostButton>}
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <GhostButton onClick={() => { setShowAccountModal(false); setEditingAccount(null) }}>Cancel</GhostButton>
              <GoldButton onClick={handleSaveAccount}>{saving ? 'Saving...' : 'Save'}</GoldButton>
            </div>
          </div>
        </Modal>
      )}

      {pendingClear && (
        <Modal title="Clear quick spends?" onClose={() => setPendingClear(null)}>
          <p style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '16px' }}>
            You updated an account balance. You have {pendingClear.count} quick spend{pendingClear.count !== 1 ? 's' : ''} ({fmt(pendingClear.total)}) this period. Clear them to avoid double-counting?
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <GhostButton onClick={() => setPendingClear(null)}>Keep them</GhostButton>
            <GoldButton onClick={confirmClearCycle}>Clear spends</GoldButton>
          </div>
        </Modal>
      )}
    </div>
  )
}
